import {
  BackSide,
  BufferGeometry,
  Color,
  DirectionalLight,
  DodecahedronGeometry,
  Float32BufferAttribute,
  Fog,
  Group,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  PMREMGenerator,
  Points,
  PointsMaterial,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { createRandom } from '../utils/random.js';
import { disposeObject3D } from '../utils/dispose.js';
import { computeTimeOfDay } from './TimeOfDay.js';

const SKY_VERTEX = /* glsl */ `
  varying vec3 vDirection;

  void main() {
    vDirection = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 topColor;
  uniform vec3 horizonColor;
  uniform vec3 groundColor;
  uniform vec3 sunColor;
  uniform vec3 sunDirection;
  uniform vec3 moonColor;
  uniform vec3 moonDirection;

  varying vec3 vDirection;

  void main() {
    vec3 direction = normalize(vDirection);
    float height = direction.y;

    vec3 color = mix(horizonColor, topColor, smoothstep(-0.01, 0.32, height));
    color = mix(groundColor, color, smoothstep(-0.14, 0.02, height));

    // Sun disc + halo.
    float sunAmount = max(dot(direction, sunDirection), 0.0);
    float disc = pow(sunAmount, 420.0) * 1.6;
    float glow = pow(sunAmount, 14.0) * 0.2;
    color += sunColor * (disc + glow);

    // Moon disc + halo (naturally hidden while the sun is up).
    float moonAmount = max(dot(direction, moonDirection), 0.0);
    float mdisc = pow(moonAmount, 600.0) * 0.9;
    float mglow = pow(moonAmount, 10.0) * 0.06;
    color += moonColor * (mdisc + mglow);

    gl_FragColor = vec4(color, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

// Sky/fog colour the world falls to during a power blackout — a near-black
// void so the only thing left reading is the player's own vehicle lights.
const BLACKOUT_FOG = new Color(0x04050a);

/**
 * Sky, sun, moon, atmosphere, distant set dressing and a little drifting dust.
 *
 * The day/night cycle lives here: `update(dt)` advances `timeOfDay` and re-applies
 * the computed palette every frame, so the whole world eases between noon and
 * midnight on its own. `setTimeOfDay(t)` jumps to a specific moment (handy for
 * debugging and for the night regression check).
 */
export class Environment {
  constructor({ scene, renderer, config }) {
    this.scene = scene;
    this.renderer = renderer;
    this.config = config;
    this.colors = config.colors;

    this.time = 0;
    this.timeOfDay = config.timeOfDay?.start ?? 0.3;
    this.cycleSeconds = config.timeOfDay?.cycleSeconds ?? 480;
    this.nightFactor = 0;

    // Power blackout: an occasional, night-only event where the grid fails.
    // Eased 0..1 so the lights dip rather than snap; it only ever arms after
    // dark (`nightFactor > 0.55`) and self-clears after `duration` seconds.
    this.blackoutEnabled = config.blackout?.enabled !== false;
    this.blackoutCfg = config.blackout ?? {};
    this.blackout = false;
    this.blackoutFactor = 0;
    this.blackoutTimer = 0;
    this.blackoutCooldown = this.blackoutCfg.startGap ?? 45;
    this.blackoutColor = new Color();

    this.root = new Group();
    this.scene.add(this.root);

    this.sunDirection = new Vector3(-0.46, 0.72, 0.34).normalize();
    this.moonDirection = new Vector3(0.46, -0.72, -0.34).normalize();
    this.environmentTexture = null;
    this.focus = new Vector3();
    // Camera-to-truck distance, refreshed each frame by Game. The blackout fog
    // is anchored to this so the truck and its lights stay inside the clear air.
    this.viewDistance = config.camera?.distance ?? 8.2;

    this.#setupRenderer();
    this.#setupSky();
    this.#setupLights();
    this.#setupEnvironmentMap();
    this.#setupDunes();
    this.#setupDust();
    this.#setupStars();

    this.applyTimeOfDay(computeTimeOfDay(this.timeOfDay));
  }

  #setupRenderer() {
    const { scene, renderer, config } = this;

    renderer.setClearColor(this.colors.fog, 1);
    scene.background = new Color(this.colors.fog);
    scene.fog = new Fog(this.colors.fog, config.render.fogNear, config.render.fogFar);
  }

  #setupSky() {
    const geometry = new SphereGeometry(450, 32, 20);
    const material = new ShaderMaterial({
      side: BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        topColor: { value: new Color(this.colors.skyTop) },
        horizonColor: { value: new Color(this.colors.skyHorizon) },
        groundColor: { value: new Color(this.colors.skyGround) },
        sunColor: { value: new Color(this.colors.sun) },
        sunDirection: { value: this.sunDirection.clone() },
        moonColor: { value: new Color(0x9fb4e0) },
        moonDirection: { value: this.moonDirection.clone() },
      },
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
    });

    this.sky = new Mesh(geometry, material);
    this.sky.frustumCulled = false;
    this.sky.renderOrder = -1;
    this.root.add(this.sky);
  }

  #setupLights() {
    const hemisphere = new HemisphereLight(0xbdd6f2, this.colors.sand, 0.45);
    this.root.add(hemisphere);
    this.hemisphere = hemisphere;

    const sun = new DirectionalLight(this.colors.sun, 3.25);
    sun.position.copy(this.sunDirection).multiplyScalar(90);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);

    const bounds = this.config.render.shadowBounds ?? 42;
    sun.shadow.camera.left = -bounds;
    sun.shadow.camera.right = bounds;
    sun.shadow.camera.top = bounds;
    sun.shadow.camera.bottom = -bounds;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 210;
    sun.shadow.bias = -0.00022;
    sun.shadow.normalBias = 0.035;

    sun.target.position.set(0, 0, 0);
    this.root.add(sun, sun.target);
    this.sun = sun;

    // Cool fill light that takes over once the sun has set. No shadows — it is
    // a soft moonlight wash, and keeping a single shadow-casting directional
    // protects the frame budget on the long street.
    const moon = new DirectionalLight(0x9fb4e0, 0.0);
    moon.position.copy(this.moonDirection).multiplyScalar(90);
    moon.target.position.set(0, 0, 0);
    this.root.add(moon, moon.target);
    this.moon = moon;
  }

  /**
   * Keep the sky dome, shadow frustum and stars centred on the player as the
   * truck travels the full 5 km street.
   */
  setFocus(focus) {
    if (!focus) return;
    this.focus.copy(focus);

    this.sun.position.copy(focus).addScaledVector(this.sunDirection, 90);
    this.sun.target.position.set(focus.x, 0.25, focus.z);
    this.sun.target.updateMatrixWorld();

    this.moon.position.copy(focus).addScaledVector(this.moonDirection, 90);
    this.moon.target.position.set(focus.x, 0.25, focus.z);
    this.moon.target.updateMatrixWorld();

    if (this.sky) this.sky.position.set(focus.x, 0, focus.z);
    if (this.dust) {
      this.dust.position.x = focus.x;
      this.dust.position.z = focus.z;
    }
    if (this.dunes) this.dunes.position.set(focus.x, 0, focus.z);
    if (this.stars) this.stars.position.set(focus.x, 0, focus.z);
  }

  #setupEnvironmentMap() {
    const pmrem = new PMREMGenerator(this.renderer);
    const room = new RoomEnvironment();
    this.environmentTexture = pmrem.fromScene(room, 0.04).texture;
    this.scene.environment = this.environmentTexture;
    this.scene.environmentIntensity = this.config.render.environmentIntensity ?? 0.35;

    room.dispose();
    pmrem.dispose();
  }

  #setupDunes() {
    if (this.config.render.dunes === false) return;

    const random = createRandom(11);
    const geometry = new DodecahedronGeometry(1, 0);
    const material = new MeshStandardMaterial({
      color: this.colors.dunes,
      roughness: 1,
      flatShading: true,
    });

    this.dunes = new Group();

    for (let i = 0; i < 16; i += 1) {
      const angle = (i / 16) * Math.PI * 2 + random.range(-0.12, 0.12);
      const radius = random.range(48, 78);
      const mesh = new Mesh(geometry, material);
      mesh.position.set(
        Math.cos(angle) * radius,
        random.range(-3.5, -1),
        Math.sin(angle) * radius,
      );
      mesh.scale.set(random.range(6, 16), random.range(3, 8), random.range(5, 14));
      mesh.rotation.y = random() * Math.PI * 2;
      this.dunes.add(mesh);
    }

    this.root.add(this.dunes);
  }

  #setupDust() {
    const random = createRandom(23);
    const count = 320;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i += 1) {
      const radius = random.range(4, 42);
      const angle = random() * Math.PI * 2;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = random.range(0.2, 12);
      positions[i * 3 + 2] = Math.sin(angle) * radius;
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

    const material = new PointsMaterial({
      color: this.colors.dust,
      size: 0.055,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    });

    this.dust = new Points(geometry, material);
    this.root.add(this.dust);
  }

  #setupStars() {
    const random = createRandom(77);
    const count = 900;
    const positions = new Float32Array(count * 3);

    for (let i = 0; i < count; i += 1) {
      const theta = random() * Math.PI * 2;
      const phi = Math.acos(2 * random() - 1);
      const r = 430;
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      // Bias the field toward the upper hemisphere so stars sit above the skyline.
      positions[i * 3 + 1] = Math.abs(r * Math.cos(phi)) * 0.92 + 24;
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

    const material = new PointsMaterial({
      color: 0xffffff,
      size: 1.6,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: false,
    });

    this.stars = new Points(geometry, material);
    this.stars.frustumCulled = false;
    this.stars.renderOrder = -0.5;
    this.root.add(this.stars);
  }

  /**
   * Push a computed time-of-day palette onto every renderer/scene property it
   * touches. Called once per frame from `update` and on demand from
   * `setTimeOfDay`.
   */
  applyTimeOfDay(p) {
    const u = this.sky.material.uniforms;
    u.topColor.value.set(p.topColor);
    u.horizonColor.value.set(p.horizonColor);
    u.groundColor.value.set(p.groundColor);
    u.sunColor.value.set(p.sunColor);
    u.moonColor.value.set(p.moonColor);
    u.sunDirection.value.copy(p.sunDirection);
    u.moonDirection.value.copy(p.moonDirection);

    this.sunDirection.copy(p.sunDirection);
    this.moonDirection.copy(p.moonDirection);

    this.scene.fog.color.set(p.fogColor);
    this.scene.fog.near = p.fogNear;
    this.scene.fog.far = p.fogFar;
    this.scene.background.set(p.fogColor);
    this.renderer.setClearColor(p.fogColor, 1);

    this.renderer.toneMappingExposure = p.exposure;

    const sunVis = smoothstep(-0.05, 0.15, p.sunDirection.y);
    this.sun.intensity = p.sunIntensity * sunVis;

    const moonVis = smoothstep(-0.1, 0.3, p.moonDirection.y);
    this.moon.intensity = p.moonIntensity * moonVis;

    this.hemisphere.color.set(p.hemiSky);
    this.hemisphere.groundColor.set(p.hemiGround);
    this.hemisphere.intensity = p.hemiIntensity;

    this.nightFactor = p.nightFactor;
    if (this.stars) this.stars.material.opacity = p.nightFactor;

    // Re-anchor the sun/moon to the current focus now that their directions
    // have changed (setFocus runs before update, so this is the final word).
    if (this.focus) {
      this.sun.position.copy(this.focus).addScaledVector(this.sunDirection, 90);
      this.sun.target.position.set(this.focus.x, 0.25, this.focus.z);
      this.sun.target.updateMatrixWorld();
      this.moon.position.copy(this.focus).addScaledVector(this.moonDirection, 90);
      this.moon.target.position.set(this.focus.x, 0.25, this.focus.z);
      this.moon.target.updateMatrixWorld();
    }

    // Power blackout override: the city goes dark, the fog closes in to a
    // couple of metres and only the player's own lights still read.
    const b = this.blackoutFactor;
    if (b > 0.001) {
      const cfg = this.blackoutCfg;
      const near = this.viewDistance + (cfg.fogNearOffset ?? 2.6);
      const far = this.viewDistance + (cfg.fogFarOffset ?? 6.0);
      const fogNear = p.fogNear + (near - p.fogNear) * b;
      const fogFar = p.fogFar + (far - p.fogFar) * b;
      this.scene.fog.near = fogNear;
      this.scene.fog.far = fogFar;
      this.renderer.toneMappingExposure = p.exposure + ((cfg.exposure ?? 0.13) - p.exposure) * b;
      this.moon.intensity *= 1 - b;
      this.hemisphere.intensity = p.hemiIntensity * (1 - 0.9 * b);
      const fogCol = this.blackoutColor.set(p.fogColor).lerp(BLACKOUT_FOG, 0.8 * b);
      this.scene.fog.color.copy(fogCol);
      this.scene.background.copy(fogCol);
      this.renderer.setClearColor(fogCol, 1);
    }
  }

  /** Jump to a specific moment of the cycle, 0..1. Used by the night check. */
  setTimeOfDay(t) {
    this.timeOfDay = ((t % 1) + 1) % 1;
    this.applyTimeOfDay(computeTimeOfDay(this.timeOfDay));
  }

  update(dt, viewDistance = 0) {
    this.time += dt;
    if (viewDistance > 0) this.viewDistance = viewDistance;
    if (this.dust) {
      this.dust.rotation.y = this.time * 0.012;
      this.dust.position.y = Math.sin(this.time * 0.15) * 0.12;
    }

    this.timeOfDay = (this.timeOfDay + dt / this.cycleSeconds) % 1;

    this.#updateBlackout(dt);
    this.applyTimeOfDay(computeTimeOfDay(this.timeOfDay));
  }

  /**
   * Occasional, night-only grid failure. Arms only once the street is properly
   * dark, fires on a low per-second probability after a cooldown gap, runs for
   * `duration` seconds, then backs off again. `blackoutFactor` eases toward the
   * target so the lights dip rather than snap.
   */
  #updateBlackout(dt) {
    const cfg = this.blackoutCfg;
    if (this.nightFactor > 0.55) {
      if (this.blackout) {
        this.blackoutTimer -= dt;
        if (this.blackoutTimer <= 0) {
          this.blackout = false;
          const lo = cfg.cooldownMin ?? 55;
          const hi = cfg.cooldownMax ?? 120;
          this.blackoutCooldown = lo + Math.random() * Math.max(0, hi - lo);
        }
      } else if (this.blackoutCooldown > 0) {
        this.blackoutCooldown -= dt;
      } else if (this.blackoutEnabled && Math.random() < dt * (cfg.chancePerSecond ?? 0.04)) {
        this.blackout = true;
        this.blackoutTimer = cfg.duration ?? 10;
      }
    } else {
      // Power failures belong to the dark. If dawn breaks mid-event the grid
      // comes back, and the next night still gets its full quiet gap first.
      this.blackout = false;
      this.blackoutTimer = 0;
      this.blackoutCooldown = Math.max(this.blackoutCooldown, cfg.startGap ?? 45);
    }

    const target = this.blackout ? 1 : 0;
    const k = 1 - Math.exp(-dt / 0.22); // ~0.22 s time constant
    this.blackoutFactor += (target - this.blackoutFactor) * k;
    if (Math.abs(target - this.blackoutFactor) < 0.004) this.blackoutFactor = target;
  }

  /** Force a power blackout (debug / testing). Suppresses random re-triggers. */
  triggerBlackout(seconds = 10) {
    this.blackout = true;
    this.blackoutTimer = seconds;
    this.blackoutCooldown = 1e9;
  }

  /** End any active blackout immediately (debug / testing). */
  clearBlackout() {
    this.blackout = false;
    this.blackoutTimer = 0;
  }

  dispose() {
    this.scene.fog = null;
    this.scene.background = null;
    this.scene.environment = null;

    if (this.environmentTexture) {
      this.environmentTexture.dispose();
      this.environmentTexture = null;
    }

    disposeObject3D(this.root);
    this.root.clear();
  }
}
